import { useCallback, useEffect, useState } from 'react';

export const safeReadStorage = (storageKey) => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage.getItem(storageKey));
  } catch {
    return false;
  }
};

export const safeWriteStorage = (storageKey) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storageKey, 'true');
  } catch {}
};

const getScrollPercent = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const scrollHeight = Math.max(
    doc.scrollHeight || 0,
    body.scrollHeight || 0,
    doc.offsetHeight || 0,
    body.offsetHeight || 0,
  );
  const viewportHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollable = scrollHeight - viewportHeight;
  if (scrollable <= 0) return 0;
  return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
};

const usePopupTrigger = ({ enabled = true, storageKey, scrollPercent, delayMs, onOpen }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (safeReadStorage(storageKey)) return undefined;

    let triggered = false;
    let timerId;
    let rafId = null;
    let scrollHandler;

    const cleanupTriggers = () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = undefined;
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = undefined;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const fire = (trigger) => {
      if (triggered) return;
      triggered = true;
      cleanupTriggers();
      setOpen(true);
      if (onOpen) onOpen(trigger);
    };

    if (typeof delayMs === 'number' && delayMs >= 0) {
      timerId = setTimeout(() => fire('delay'), delayMs);
    }

    if (typeof scrollPercent === 'number' && scrollPercent > 0) {
      scrollHandler = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (getScrollPercent() >= scrollPercent) {
            fire('scroll');
          }
        });
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
      scrollHandler();
    }

    return cleanupTriggers;
  }, [enabled, storageKey, scrollPercent, delayMs, onOpen]);

  const dismiss = useCallback(() => {
    setOpen(false);
    safeWriteStorage(storageKey);
  }, [storageKey]);

  return { open, dismiss };
};

export default usePopupTrigger;
